/**
 * Dual-currency policy (Faza 4 of the warehouse-only plan).
 *
 * Decision D3: ZAR is the base currency and the ONLY price source (the TodAi
 * price lists are in ZAR). USD is DERIVED for the USA market via a single
 * configurable FX rate — there is no USD price data. All money is stored and
 * computed in base ZAR; conversion + formatting happen only at the display edge
 * (see src/context/CurrencyContext.tsx).
 *
 * This is deliberately separate from src/lib/currency.ts, whose `ratePerEur`
 * is 1.0 by design (a relabel-only selector). Do NOT route FX through that.
 */

export type RegionCurrency = 'ZAR' | 'USD';

/** Everything is priced and stored in this currency. */
export const PLATFORM_BASE_CURRENCY: RegionCurrency = 'ZAR';

/**
 * USD per 1 ZAR. The one FX knob — set VITE_FX_USD_PER_ZAR in the environment
 * and refresh periodically (see F7 maintenance). Default ≈ R18.5 / USD.
 */
export const USD_PER_ZAR = Number(import.meta.env?.VITE_FX_USD_PER_ZAR ?? 0.054);

/** Convert a base-ZAR amount into the target display currency. No-op for ZAR. */
export function convertFromBaseZar(amountZar: number, target: RegionCurrency): number {
  if (!Number.isFinite(amountZar)) return 0;
  return target === 'ZAR' ? amountZar : amountZar * USD_PER_ZAR;
}

/** Convert a display-currency amount back to base ZAR (for inputs/quotes). */
export function convertToBaseZar(amount: number, from: RegionCurrency): number {
  if (!Number.isFinite(amount)) return 0;
  return from === 'ZAR' ? amount : amount / USD_PER_ZAR;
}

export interface RegionSignals {
  /** Compliance region from user preferences ('za' | 'us'). */
  region?: 'us' | 'za';
  /** Explicit currency preference (user_preferences.default_currency). */
  currencyPref?: string;
  /** Active UI language (af/zu imply the ZA market). */
  language?: string;
  /** What to show when nothing else decides — the platform's own base currency by default. */
  fallback?: RegionCurrency;
}

/**
 * Resolve the display currency from the available signals. Precedence:
 * explicit region → explicit currency preference → language → base (ZAR).
 * There is no geo/IP detection in the app, so these are the only inputs.
 */
export function resolveRegionCurrency(signals: RegionSignals): RegionCurrency {
  if (signals.region === 'za') return 'ZAR';
  if (signals.region === 'us') return 'USD';
  if (signals.currencyPref === 'ZAR' || signals.currencyPref === 'USD') return signals.currencyPref;
  const lang = (signals.language || '').toLowerCase();
  if (lang === 'af' || lang === 'zu') return 'ZAR';
  return signals.fallback ?? PLATFORM_BASE_CURRENCY;
}
